import type { Effective } from "./effective";
import { ALL_PERMISSIONS, type Permission } from "./permissions";
import { capabilityLabel } from "./capabilities";

/**
 * 2.795 — the console's `Permission` names, decided from the platform's own
 * answer about the signed-in subject (`GET /v1/people/{sub}/permissions`, S-709,
 * ADR-106). SPONSOR RULING (2026-10-02, relayed by the coordinator): once the
 * self read lands, it feeds `permissionsKnown`; the console hides or disables
 * what the person cannot use AS THE PLATFORM REPORTS, and a failed read keeps
 * today's fail-open behaviour. The platform still enforces every action.
 *
 * Each mapping is the requirement the gateway itself applies to the route the
 * control calls (`control/cmd/gateway/server.go` and the api packages at
 * 27f7ea58, and the contract's "Authorized" lines):
 *
 * - organisation `member`: the reads. Every member reads every project
 *   (`viewer` is `member from workspace` in the model), and the org-scoped reads
 *   (`/v1/people`, `/v1/issues`, risk weights, promotion rule) need `member`.
 * - organisation `admin`: API keys (every verb, read included), the roster's
 *   writes, and roles (every role verb, the list included).
 * - project capability: targets' writes `can_configure_targets`; launch, abort,
 *   a target's scan and a schedule's writes `can_launch_scan`; issue moves and
 *   promotion `can_manage_issues`; a report request `can_request_reports`; the
 *   project's AI toggle project `admin`.
 * - projects:manage: create is workspace `admin`, rename and delete project
 *   `admin`; so organisation admin, any workspace admin, or the project's admin.
 *
 * A permission with NO platform counterpart (`UNMAPPED`) is never decided: it
 * stays shown. Absence of a mapping is not a denial. An entry the console does
 * not recognise (`known: false`) neither grants nor denies anything.
 */

type Rule =
  | { scope: "organisation"; relation: "admin" | "member" }
  | { scope: "project"; relation: string }
  | { scope: "manage-projects" };

const member: Rule = { scope: "organisation", relation: "member" };
const orgAdmin: Rule = { scope: "organisation", relation: "admin" };
const project = (relation: string): Rule => ({ scope: "project", relation });

export const PLATFORM_MAP: Partial<Record<Permission, Rule>> = {
  "targets:read": member,
  "scans:read": member,
  "vulnerabilities:read": member,
  "issues:read": member,
  "reports:read": member,
  "schedules:read": member,
  "team:read": member,
  "projects:read": member,
  "api-keys:read": orgAdmin,
  "api-keys:manage": orgAdmin,
  "team:manage": orgAdmin,
  "roles:read": orgAdmin,
  "roles:manage": orgAdmin,
  "targets:create": project("can_configure_targets"),
  "targets:update": project("can_configure_targets"),
  "targets:delete": project("can_configure_targets"),
  "targets:scan": project("can_launch_scan"),
  "scans:run": project("can_launch_scan"),
  "scans:stop": project("can_launch_scan"),
  "schedules:manage": project("can_launch_scan"),
  "issues:transition": project("can_manage_issues"),
  "reports:generate": project("can_request_reports"),
  "ai-testing:toggle": project("admin"),
  // 2.973 (demo-path review): organisation configuration. The writes it gates
  // are organisation `admin` on the contract: POST /v1/risk-weights and POST
  // /v1/promotion-rule ("Authorized `admin` of the caller's organisation"),
  // PUT /v1/orgs/{id}/ai-budget; and the pages behind it (Organisation
  // Settings, the project's Settings and Excluded Hours) hold organisation
  // policy. Unmapped, every role saw them and was refused on use.
  // `scan-profiles:manage` stays unmapped: the menu is readable by every member
  // and the contract has no write for it.
  "settings:manage": orgAdmin,
  "projects:manage": { scope: "manage-projects" },
};

/** Console permissions the platform's answer cannot decide: always shown. */
export const UNMAPPED: Permission[] = ALL_PERMISSIONS.filter((p) => !(p in PLATFORM_MAP));

const holds = (rs: { name: string; known: boolean }[], relation: string) =>
  rs.some((r) => r.known && r.name === relation);

/** The project relations to look at: the named project's, or every project's when none is named. */
function projectRelations(e: Effective, projectId: string | undefined) {
  if (projectId) {
    // "A project absent from `projects` is one the subject holds nothing on."
    return [e.projects.find((p) => p.projectId === projectId)?.relations ?? []];
  }
  return e.projects.map((p) => p.relations);
}

/**
 * Whether the platform's answer allows `permission`, or `undefined` when it has
 * nothing to say (unmapped). `projectId` scopes a project capability to that
 * project; without one, holding it on ANY project allows it, so a control on a
 * page that spans projects is never hidden from someone who may use it somewhere.
 */
export function decide(e: Effective, permission: Permission, projectId?: string): boolean | undefined {
  const rule = PLATFORM_MAP[permission];
  if (!rule) return undefined;
  switch (rule.scope) {
    case "organisation":
      return holds(e.organisation, rule.relation);
    case "project":
      return projectRelations(e, projectId).some((rs) => holds(rs, rule.relation));
    case "manage-projects":
      return holds(e.organisation, "admin")
        || e.workspaces.some((w) => holds(w.relations, "admin"))
        || projectRelations(e, projectId).some((rs) => holds(rs, "admin"));
  }
}

/**
 * Why a control is disabled, in the platform's terms. The answer carries no
 * reason text of its own, so this names what the platform REPORTED, neutrally;
 * it never says "you can't".
 */
export function deniedReason(permission: Permission, projectId?: string): string | undefined {
  const rule = PLATFORM_MAP[permission];
  if (!rule) return undefined;
  const where = projectId ? "on this project" : "on any project";
  switch (rule.scope) {
    case "organisation":
      return rule.relation === "admin"
        ? "The platform reports you are not an administrator of this organisation."
        : "The platform reports you are not a member of this organisation.";
    case "project":
      return `The platform reports you do not hold ${capabilityLabel(rule.relation)} ${where}.`;
    case "manage-projects":
      return `The platform reports you do not administer this organisation, any of its workspaces, or ${projectId ? "this project" : "any project"}.`;
  }
}
